import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { useComposableFilter } from '@/components/ComposableFilter';
import Icon from '@/components/Icon/Icon';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import {
  CostOverviewFilterBar,
  type CostGroupBy,
  type CostOverviewViewMode,
  type ComparisonTarget,
} from '@/features/contract-and-cost/cost';
import {
  getMonthsInPeriod,
  parsePeriodStart,
} from '@/features/contract-and-cost/period';
import { CostStatCards } from '@/features/device/contract-and-cost/components/CostStatCards';
import { CostSummaryChart } from '@/features/device/contract-and-cost/components/CostSummaryChart';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { CostOverviewTable } from './-components/CostOverviewTable';
import { DepartmentCostTable } from './-components/DepartmentCostTable';
import { MemberCostTable } from './-components/MemberCostTable';
import { MonthlyCostTable } from './-components/MonthlyCostTable';
import {
  useDeviceCostByDepartment,
  useDeviceCostByMember,
  useDeviceCostOverviewList,
  useDeviceCostOverviewSummary,
  useDeviceMonthlyCostList,
} from './-query';

const SearchSchema = z.object({
  groupBy: z.enum(['catalog', 'member', 'department']).default('catalog'),
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  comparisonTarget: z
    .enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH'])
    .default('PREVIOUS_MONTH'),
});

export const Route = createFileRoute(
  '/_staff/devices/_list/contract-and-cost/_contract-and-cost/cost/$period/',
)({
  component: CostOverviewPage,
  validateSearch: zodValidator(SearchSchema),
});

function CostOverviewPage() {
  const { period } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const summaryRegionId = 'cost-overview-summary-region';

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.costOverview',
  });

  const isCatalog = search.groupBy === 'catalog';
  const isMember = search.groupBy === 'member';
  const isDepartment = search.groupBy === 'department';
  const isMonthly = isCatalog && search.viewMode === 'monthly';
  const monthOptions = useMemo(() => getMonthsInPeriod(period), [period]);
  const currentMonth = search.month ?? new Date().getMonth() + 1;

  const [showSummary, setShowSummary] = useState(false);

  // ページングは URL search params を単一の正とする（useState との二重管理を避け、
  // ブラウザの戻る/進む時も表示ページと API リクエストがズレないようにする）。
  const currentPage = search.pageIndex + 1;
  const { pageSize } = search;

  const filter = useComposableFilter<Record<string, never>>({
    filterDefs: [],
    filterState: {},
    onFilterStateChange: () => {},
  });

  const parsedPeriodStart = parsePeriodStart(period);
  const periodDate = new Date(
    Date.UTC(parsedPeriodStart.year, parsedPeriodStart.month - 1, 1),
  );
  const targetYearMonth = periodDate.toISOString();

  const { data: summary } = useDeviceCostOverviewSummary({
    targetYearMonth,
  });
  const transitionQuery = useDeviceCostOverviewList(
    {
      targetYearMonth,
      costComparisonTarget: search.comparisonTarget,
      page: currentPage,
      pageSize,
      freeText: search.freeText,
    },
    isCatalog && !isMonthly,
  );
  const monthlyQuery = useDeviceMonthlyCostList(
    {
      period,
      viewMode: 'monthly',
      month: currentMonth,
      page: currentPage,
      pageSize,
      freeText: search.freeText,
    },
    isMonthly,
  );
  const memberQuery = useDeviceCostByMember(
    {
      targetYearMonth,
      costComparisonTarget: search.comparisonTarget,
      page: currentPage,
      pageSize,
      freeText: search.freeText,
    },
    isMember,
  );
  const departmentQuery = useDeviceCostByDepartment(
    {
      targetYearMonth,
      costComparisonTarget: search.comparisonTarget,
    },
    isDepartment,
  );

  const handlePageChange = useCallback(
    (page: number) => {
      navigate({
        search: (prev) => ({ ...prev, pageIndex: page - 1 }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      navigate({
        search: (prev) => ({ ...prev, pageIndex: 0, pageSize }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleGroupByChange = useCallback(
    (groupBy: CostGroupBy) => {
      navigate({
        search: (prev) => ({
          ...prev,
          groupBy,
          // 個人別/組織別は推移のみ対応のため月選択をクリアする
          viewMode: groupBy === 'catalog' ? prev.viewMode : 'transition',
          month: undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleViewModeChange = useCallback(
    (mode: CostOverviewViewMode) => {
      navigate({
        search: (prev) => ({
          ...prev,
          viewMode: mode,
          month: mode === 'monthly' ? currentMonth : undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate, currentMonth],
  );

  const handleMonthChange = useCallback(
    (month: number) => {
      navigate({
        search: (prev) => ({ ...prev, month }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleComparisonTargetChange = useCallback(
    (target: ComparisonTarget) => {
      navigate({
        search: (prev) => ({ ...prev, comparisonTarget: target }),
      });
    },
    [navigate],
  );

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          freeText: e.target.value || undefined,
          pageIndex: 0,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // 組織別はフルテキスト検索の入力を持たない（Contract に freeText が無い）
  const showSearch = !isDepartment;

  return (
    <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {summary && (
          <VStack className="gap-5">
            <CostStatCards summary={summary} />
            <CostSummaryChart data={summary.months} months={monthOptions} />
          </VStack>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1 transition-colors"
          aria-expanded={showSummary}
          aria-controls={summaryRegionId}
          onClick={() => setShowSummary((prev) => !prev)}
        >
          <Icon
            iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            className={cn('size-3.5')}
          />
          <Text
            className={cn(
              'text-labelSmallReg',
              'text-neutral-muted-on-fill',
              'hover:text-neutral-on-fill',
            )}
          >
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <CostOverviewFilterBar
        groupBy={search.groupBy}
        onGroupByChange={handleGroupByChange}
        viewMode={search.viewMode}
        onViewModeChange={handleViewModeChange}
        selectedMonth={isMonthly ? currentMonth : undefined}
        onSelectedMonthChange={handleMonthChange}
        monthOptions={monthOptions}
        searchValue={showSearch ? (search.freeText ?? '') : undefined}
        onSearchChange={showSearch ? handleSearchChange : undefined}
        filter={filter}
        comparisonTarget={search.comparisonTarget}
        onComparisonTargetChange={handleComparisonTargetChange}
      />

      {isMember && (
        <MemberCostTable
          data={memberQuery.data}
          isPending={memberQuery.isPending}
          isError={memberQuery.isError}
          monthList={monthOptions}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          className="flex-1 min-h-0"
        />
      )}

      {isDepartment && (
        <DepartmentCostTable
          data={departmentQuery.data}
          isPending={departmentQuery.isPending}
          isError={departmentQuery.isError}
          monthList={monthOptions}
          className="flex-1 min-h-0"
        />
      )}

      {isCatalog &&
        (isMonthly ? (
          <MonthlyCostTable
            data={monthlyQuery.data?.items ?? []}
            isPending={monthlyQuery.isPending}
            totalCount={monthlyQuery.data?.count}
            pageCount={monthlyQuery.data?.maxPage}
            pagination={{
              pageIndex: search.pageIndex,
              pageSize,
            }}
            onPaginationChange={(next) => {
              navigate({
                search: (prev) => ({
                  ...prev,
                  pageIndex: next.pageIndex,
                  pageSize: next.pageSize,
                }),
                replace: true,
              });
            }}
            className="flex-1 min-h-0"
          />
        ) : (
          <CostOverviewTable
            data={transitionQuery.data?.items ?? []}
            isPending={transitionQuery.isPending}
            months={monthOptions}
            totalCount={transitionQuery.data?.count}
            pageCount={transitionQuery.data?.maxPage}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="flex-1 min-h-0"
            period={period}
          />
        ))}
    </div>
  );
}
